import { useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { AlertTriangle, Loader2, Trash2 } from "lucide-react";

/**
 * Self-serve account deletion (required by App Store Guideline 5.1.1(v)).
 * Two-step: open dialog -> type DELETE -> permanent deletion + sign out.
 */
export function DeleteAccountCard() {
  const [open, setOpen] = useState(false);
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const canDelete = confirm === "DELETE";

  const handleDelete = async () => {
    if (!canDelete) return;
    setBusy(true);
    try {
      const { data, error } = await supabase.functions.invoke("delete-account", {
        body: { confirm: "DELETE" },
      });
      if (error) throw error;
      if (!data?.success) throw new Error(data?.error || "Deletion failed");

      toast.success("Your account has been permanently deleted.");
      try {
        await supabase.auth.signOut({ scope: "local" });
      } catch {
        // session is already invalid after deletion
      }
      window.location.replace("/");
    } catch (e) {
      console.error("delete account error", e);
      toast.error("We couldn't delete your account. Please contact support at support@lanerunner.com.");
      setBusy(false);
    }
  };

  return (
    <Card className="p-6 mb-6 border-destructive/30">
      <div className="flex items-start gap-3 mb-4">
        <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-destructive/10">
          <AlertTriangle className="h-5 w-5 text-destructive" />
        </div>
        <div>
          <h2 className="text-xl font-semibold">Delete Account</h2>
          <p className="text-sm text-muted-foreground">
            Permanently delete your LaneRunner account and personal data.
          </p>
        </div>
      </div>

      <ul className="mb-5 space-y-1.5 text-sm text-muted-foreground list-disc pl-5">
        <li>Your profile, notification settings, saved searches and messages are removed.</li>
        <li>Your posted loads, trucks, bids and quotes are deleted or anonymized.</li>
        <li>
          Your subscription ends immediately — you will not be charged again and your current
          billing period is not refunded.
        </li>
        <li>You lose access right away and cannot log back in.</li>
        <li>This cannot be undone — you would need to create a new account to return.</li>
      </ul>


      <Button variant="destructive" onClick={() => setOpen(true)} className="gap-2">
        <Trash2 className="h-4 w-4" /> Delete my account
      </Button>

      <Dialog open={open} onOpenChange={(v) => !busy && setOpen(v)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Delete your account permanently?</DialogTitle>
            <DialogDescription>
              This immediately and permanently deletes your account and personal data. Active
              subscriptions are cancelled. This action cannot be undone.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-2">
            <Label htmlFor="delete-confirm">Type DELETE to confirm</Label>
            <Input
              id="delete-confirm"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              placeholder="DELETE"
              autoComplete="off"
              autoCapitalize="characters"
            />
          </div>

          <DialogFooter className="gap-2 sm:gap-2">
            <Button variant="outline" onClick={() => setOpen(false)} disabled={busy}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDelete} disabled={!canDelete || busy}>
              {busy ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Deleting…
                </>
              ) : (
                "Delete permanently"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
